import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onStay, onExit }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-gray-800 bg-opacity-75 p-4">
      <div className="bg-white rounded-lg shadow-2xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Unsaved Changes</h3>
        <p className="text-gray-600 mb-6">
          You have unsaved scanned data. Are you sure you want to exit without saving?
        </p>
        <div className="flex justify-center gap-1">
          <button
            className="bg-gray-300 text-gray-800 px-8 py-2 rounded-md hover:bg-gray-400 transition-colors duration-300"
            onClick={onStay}
          >
            Stay
          </button>
          <button
            className="bg-red-500 text-white px-8 py-2 rounded-md hover:bg-red-600 transition-colors duration-300"
            onClick={onExit}
          >
            Exit
          </button>
        </div>
      </div>
    </div>
  );
};

const ScanningModal = ({ selectedFDN, modalOpen, toggleModal, scannerCount, scannedData, setScannedData }) => {
  const [currentScanner, setCurrentScanner] = useState(0);
  const [scannedScanners, setScannedScanners] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scannerInputRef = useRef(null);
  const [bufferedInput, setBufferedInput] = useState("");
  const timeoutRef = useRef(null);
  const [previousScanner, setPreviousScanner] = useState(null);
  const [initialData, setInitialData] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false); // State for confirmation modal
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Fetch previously scanned modules when the modal opens
  
  useEffect(() => {
    const fetchScannedModules = async () => {
      if (selectedFDN && modalOpen) {
        try {
          const response = await axios.get(`${baseURL}get_modules_from_fdn`, {
            params: { 
              projectId: localStorage.getItem('selectedProject'),
              fdnAddress: selectedFDN }
          });

          const { moduleSno } = response.data || {};
          const existingModules = moduleSno || [];

          setScannedData(prev => ({
            ...prev,
            [selectedFDN]: existingModules
          }));
          setScannedScanners(existingModules.map((_, idx) => idx));
          setCurrentScanner(existingModules.length);
          setInitialData(existingModules);

          console.log(`[API] Fetched modules for ${selectedFDN}:`, existingModules);
        } catch (error) {
          console.error('[API] Error fetching modules:', error);
          setErrorMessage('Failed to load previous scans. Starting fresh.');
          setTimeout(() => setErrorMessage(""), 3000);
        }
      }
    };

    fetchScannedModules();
  }, [selectedFDN, modalOpen, setScannedData]);

  // Scanner input handling
  useEffect(() => {
    const handleScannerInput = (event) => {
      const newValue = event.target.value.trim().toUpperCase();
      console.log(`[Scanner] Raw input: "${newValue}"`);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setBufferedInput(newValue);

      timeoutRef.current = setTimeout(() => {
        if (newValue && modalOpen && selectedFDN) {
          console.log(`[Scanner] Full QR code detected: "${newValue}"`);
          const existingData = scannedData[selectedFDN] || [];
          if (existingData.includes(newValue)) {
            console.error(`[Error] Duplicate Serial Number Detected: "${newValue}"`);
            setErrorMessage(`Duplicate Serial Number Detected: "${newValue}"`);
            setTimeout(() => setErrorMessage(""), 3000);
          } else {
            setErrorMessage("");
            handleScan(newValue);
            if (previousScanner !== null) {
              setCurrentScanner(previousScanner);
              setPreviousScanner(null);
            } else if (currentScanner < scannerCount - 1) {
              setCurrentScanner(prev => prev + 1);
            }
          }
          event.target.value = "";
          setBufferedInput("");
        }
      }, 100);
    };

    const inputField = scannerInputRef.current;
    if (inputField && modalOpen) {
      console.log("[Scanner] Attaching input listener");
      inputField.addEventListener("input", handleScannerInput);
      inputField.focus();
    }

    return () => {
      if (inputField && modalOpen) {
        console.log("[Scanner] Removing input listener");
        inputField.removeEventListener("input", handleScannerInput);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [modalOpen, selectedFDN, currentScanner, scannedData, previousScanner]);

  const handleScan = (serialNumber, index = currentScanner) => {
    if (index < scannerCount && selectedFDN) {
      setScannedData((prev) => {
        const updatedFDNData = [...(prev[selectedFDN] || Array(scannerCount).fill(null))];
        updatedFDNData[index] = serialNumber;
        return {
          ...prev,
          [selectedFDN]: updatedFDNData,
        };
      });
      if (!scannedScanners.includes(index)) {
        setScannedScanners((prev) => [...prev, index]);
      }
    }
  };

  const handleGridBoxClick = (index) => {
    if (scannedScanners.includes(index)) {
      setPreviousScanner(currentScanner);
      setCurrentScanner(index);
      setErrorMessage(`Scan to replace serial number for Panel ${index + 1}`);
      scannerInputRef.current.focus();
    }
  };

  // Check for unsaved changes
  const hasUnsavedChanges = () => {
    const currentData = scannedData[selectedFDN] || [];
    const filteredCurrentData = currentData.filter(Boolean);
    const filteredInitialData = initialData.filter(Boolean);
    
    if (filteredCurrentData.length !== filteredInitialData.length) return true;
    return filteredCurrentData.some((item, idx) => item !== filteredInitialData[idx]);
  };

  // Handle modal close with confirmation
  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setShowConfirmModal(true); // Show confirmation modal
    } else {
      toggleModal(null); // Close directly if no unsaved changes
    }
  };

  const handleStay = () => {
    setShowConfirmModal(false); // Close confirmation modal, stay in scanning modal
  };

  const handleExit = () => {
    setShowConfirmModal(false); // Close confirmation modal
    toggleModal(null); // Exit scanning modal without saving
  };

  // Submit data to /assign_modules_to_fdn
  const submitScannedData = async () => {
    if (!selectedFDN || !scannedData[selectedFDN]) {
      setErrorMessage("No data to submit.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const payload = {
        projectId: localStorage.getItem('selectedProject'),
        fdnAddress: selectedFDN,
        moduleSerialNumbers: scannedData[selectedFDN].filter(Boolean)
      };

      console.log("[API] Submitting to /assign_modules_to_fdn:", payload);

      const response = await axios.post(`${baseURL}assign_modules_to_fdn`, payload);

      console.log("[API] Response:", response.data);
      alert("Modules assigned successfully!");
      setInitialData(scannedData[selectedFDN].filter(Boolean));
      toggleModal(null);
    } catch (error) {
      console.error("[API] Error submitting data:", error);
      setErrorMessage(error.response?.data?.error || "Failed to submit data. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ScannerGrid component
  const ScannerGrid = ({ scannerCount, currentScanner, scannedScanners }) => {
    const gridOrder = [];
    for (let i = 0; i < scannerCount; i += 2) {
      gridOrder.push(i + 1);
      if (i + 2 <= scannerCount) gridOrder.push(i + 2);
    }

    return (
      <div className="relative p-4 bg-[#424752] rounded-lg shadow-md">
        <div className="p-4 overflow-x-auto">
          <div className="flex flex-col gap-1 min-w-max">
            <div className="flex gap-1">
              {gridOrder.filter((_, idx) => idx % 2 !== 0).map((index) => (
                <GridBox
                  key={index}
                  index={index}
                  isCurrent={index - 1 === currentScanner}
                  isScanned={scannedScanners.includes(index - 1)}
                  onClick={() => handleGridBoxClick(index - 1)}
                />
              ))}
            </div>
            <div className="flex gap-1">
              {gridOrder.filter((_, idx) => idx % 2 === 0).map((index) => (
                <GridBox
                  key={index}
                  index={index}
                  isCurrent={index - 1 === currentScanner}
                  isScanned={scannedScanners.includes(index - 1)}
                  onClick={() => handleGridBoxClick(index - 1)}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="absolute -bottom-6 left-0 text-sm text-gray-600 font-bold">↑ Start Here (Panel 1)</div>
      </div>
    );
  };

  const GridBox = ({ index, isCurrent, isScanned, onClick }) => {
    let backgroundColor = 'bg-gray-100';
    if (isCurrent) backgroundColor = 'bg-yellow-400 animate-pulse';
    else if (isScanned) backgroundColor = 'bg-green-500';

    return (
      <div
        className={`w-20 h-20 flex flex-col items-center justify-center rounded-md shadow-sm ${backgroundColor} transition-all duration-300 ${isScanned ? 'cursor-pointer' : 'cursor-default'}`}
        onClick={isScanned ? onClick : null}
      >
        <img
          src={isScanned ? "https://www.svgrepo.com/show/460069/scan-alt.svg" : "https://www.svgrepo.com/show/498368/scan.svg"}
          alt={isScanned ? "Scanned" : "QR Code"}
          className={`w-10 h-10 ${isScanned ? 'filter brightness-0 invert' : 'filter brightness-0'}`}
        />
        <span className="mt-1 text-sm font-semibold text-gray-700">Panel {index}</span>
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-gray-800 bg-opacity-75 p-4">
        <div className="bg-white rounded-lg shadow-2xl w-full mx-4 sm:mx-6 lg:mx-8 overflow-y-auto max-h-[90vh]">
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Solar Panel Scanning for {selectedFDN}</h2>
            <ScannerGrid scannerCount={scannerCount} currentScanner={currentScanner} scannedScanners={scannedScanners} />
            <div className="mt-6 pt-6">
              <input
                ref={scannerInputRef}
                id="scanner-input"
                type="text"
                placeholder="Scan QR code here..."
                className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 mb-4 uppercase"
                autoFocus
              />
              {errorMessage && (
                <p className="text-red-600 text-sm font-medium mb-4 bg-red-100 p-2 rounded-md">{errorMessage}</p>
              )}
              <p className="text-lg font-semibold text-gray-700 mb-2">Scanned Serial Numbers:</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 list-disc pl-6 text-gray-600">
                {scannedData[selectedFDN]?.map((serial, idx) => (
                  serial && <li key={idx} className="py-1">MODULE {idx + 1}: {serial}</li>
                ))}
              </ul>
            </div>
            <div className="flex justify-between mt-6">
              <button
                className="bg-[#424752] text-white px-6 py-2 rounded-md hover:bg-gray-600 transition-colors duration-300 disabled:opacity-50"
                onClick={submitScannedData}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Scanning"}
              </button>
              <button
                className="bg-[#424752] text-white px-6 py-2 rounded-md hover:bg-gray-600 transition-colors duration-300"
                onClick={handleClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={showConfirmModal}
        onStay={handleStay}
        onExit={handleExit}
      />
    </>
  );
};

export default ScanningModal;